import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, FileText, Sparkles } from 'lucide-react';
import MortgageService from '../../services/MortgageService';
import { Card, Input, InputGroup, FormSection, toast, MoneyInput } from '../../components/v2/V2Components';
import { sanitizeNumeric } from '../../utils/numericInput';
import { todayLocalDate, mortgageMaturityDate } from '../../utils/dateHelpers';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

// Hides the native number-input spinner (up/down steppers) in WebKit + Firefox.
const NO_SPINNER =
    '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

// A native <input type="date"> can emit a 5-digit year (e.g. "20255-08-01");
// reject anything that isn't a clean 4-digit-year date before it reaches the API.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Format-valid AND a real calendar date — DATE_RE alone passes 2026-02-31.
const isRealDate = (s) => {
    if (!DATE_RE.test(s)) return false;
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

// Money fields that pass through sanitizeNumeric before submit.
const NUMERIC_FIELDS = [
    'total_amount_due',
    'principal',
    'interest',
    'escrow',
    'fees',
    'additional_payment',
    'past_due_amount',
    'principal_balance',
    'escrow_balance',
    'interest_rate',
];

const initialFormState = {
    id: null,
    statement_date: '',
    due_date: '',
    total_amount_due: '',
    principal: '',
    interest: '',
    escrow: '',
    fees: '',
    additional_payment: '',
    past_due_amount: '',
    principal_balance: '',
    escrow_balance: '',
    interest_rate: '',
    notes: '',
};

const MortgageStatementForm = () => {
    const { houseId, mortgageId, statementId } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!statementId;

    usePageView(ANALYTICS_EVENTS.MORTGAGE_STATEMENT_FORM_VIEWED, { is_edit_mode: isEditMode });

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [formData, setFormData] = useState(initialFormState);
    const [file, setFile] = useState(null);
    const [existingFile, setExistingFile] = useState(null); // { url, name }
    const [maturityDate, setMaturityDate] = useState(''); // upper bound for due date

    const backToMortgage = `/houses/${houseId}/mortgages/${mortgageId}`;
    const today = todayLocalDate();
    // Due-date upper bound: the loan's maturity, or — when it's unknown — 5 years out.
    const fiveYearsOut = (() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 5);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    })();
    const dueDateMax = maturityDate || fiveYearsOut;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Parent loan: its maturity bounds the due date, and (on create)
                // its rate prefills the statement's interest rate.
                const m = await MortgageService.getMortgage(mortgageId);
                if (!cancelled && m) {
                    setMaturityDate(m.maturity_date || mortgageMaturityDate(m.origination_date, m.term_months) || '');
                }

                if (isEditMode) {
                    const s = await MortgageService.getStatement(statementId);
                    if (cancelled) return;
                    setFormData({
                        id: s.id,
                        statement_date: s.statement_date || '',
                        due_date: s.due_date || '',
                        total_amount_due: s.total_amount_due ?? '',
                        principal: s.principal ?? '',
                        interest: s.interest ?? '',
                        escrow: s.escrow ?? '',
                        fees: s.fees ?? '',
                        additional_payment: s.additional_payment ?? '',
                        past_due_amount: s.past_due_amount ?? '',
                        principal_balance: s.principal_balance ?? '',
                        escrow_balance: s.escrow_balance ?? '',
                        interest_rate: s.interest_rate ?? '',
                        notes: s.notes || '',
                    });
                    if (s.url) {
                        setExistingFile({ url: s.url, name: s.original_filename || s.filename || 'View file' });
                    }
                } else if (!cancelled && m && m.interest_rate != null) {
                    setFormData((prev) => ({
                        ...prev,
                        interest_rate: prev.interest_rate || String(m.interest_rate),
                    }));
                }
            } catch (error) {
                if (!cancelled) {
                    toast.error?.('Failed to load mortgage statement');
                    console.error(error);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [statementId, mortgageId, isEditMode]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Upload & autofill: send the chosen file to the extract endpoint, prefill the
    // form with what came back, and keep the same file as the attachment so the
    // user doesn't pick it twice. Extracted fields are a starting point — the user
    // reviews/edits before saving (nothing is persisted by the extract call).
    const handleAutofill = async (selectedFile) => {
        if (!selectedFile) return;
        setFile(selectedFile);
        try {
            setExtracting(true);
            const { statement } = await MortgageService.extractStatement(mortgageId, selectedFile);
            if (!statement) {
                toast.error?.('No fields could be read from that file');
                return;
            }
            setFormData((prev) => {
                const next = { ...prev };
                // Only overwrite a field when the scan actually returned a value, so
                // a partial statement doesn't blank out anything already typed.
                Object.entries(statement).forEach(([key, value]) => {
                    if (key in next && value !== null && value !== undefined && value !== '') {
                        next[key] = String(value);
                    }
                });
                return next;
            });
            toast.success?.('Statement read — review the fields before saving');
        } catch (error) {
            toast.error?.(error.message || 'Could not read that statement file');
        } finally {
            setExtracting(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!formData.total_amount_due || !formData.due_date) {
            toast.error?.('Total amount due and payment due date are required');
            return;
        }
        if (formData.statement_date && !isRealDate(formData.statement_date)) {
            toast.error?.('Statement date must be a valid date with a 4-digit year');
            return;
        }
        if (!isRealDate(formData.due_date)) {
            toast.error?.('Payment due date must be a valid date with a 4-digit year');
            return;
        }
        if (formData.statement_date && formData.statement_date > today) {
            toast.error?.('Statement date can’t be in the future');
            return;
        }
        if (formData.due_date > dueDateMax) {
            toast.error?.(
                maturityDate
                    ? 'Payment due date can’t be after the loan’s maturity date'
                    : 'Payment due date can’t be more than 5 years in the future',
            );
            return;
        }

        try {
            setSaving(true);

            const payload = { ...formData };
            // Drop the id from the payload; it's path-only.
            delete payload.id;
            NUMERIC_FIELDS.forEach((field) => {
                payload[field] = sanitizeNumeric(formData[field]);
            });
            // Blank text/date fields go out as null: the update endpoint treats
            // null as "clear" and rejects '' as an invalid date, which made a
            // statement saved without a statement date impossible to edit.
            ['statement_date', 'notes'].forEach((field) => {
                if (payload[field] === '') payload[field] = null;
            });

            if (isEditMode) {
                await MortgageService.updateStatement(formData.id, payload);
                toast.success?.('Statement updated');
            } else {
                await MortgageService.createStatement(mortgageId, { ...payload, file });
                toast.success?.('Statement added');
            }

            navigate(backToMortgage);
        } catch (error) {
            toast.error?.(error.message || 'Failed to save statement');
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <Loader2 className="animate-spin text-[var(--primary)]" size={48} />
            </div>
        );

    const moneyField = (name, label, required = false) => (
        <InputGroup label={label} required={required}>
            <MoneyInput name={name} value={formData[name]} onChange={handleInputChange} required={required} />
        </InputGroup>
    );

    return (
        <div className="animate-fade-in max-w-4xl mx-auto pb-10">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-[var(--text-main)] mb-1">
                        {isEditMode ? 'Edit Statement' : 'Add Mortgage Statement'}
                    </h1>
                    <p className="text-[var(--text-secondary)]">
                        {isEditMode ? 'Update this statement’s details' : 'Record a statement for this mortgage'}
                    </p>
                </div>
                {/* gap, not space-x: space-x-* uses child margins, which break
                    across the rows this group can now wrap onto. */}
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(backToMortgage)}
                        className="px-4 py-2 border border-[var(--border)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="statement-form"
                        disabled={saving}
                        className="flex items-center space-x-2 px-6 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all font-medium disabled:opacity-70"
                    >
                        {saving && <Loader2 size={16} className="animate-spin" />}
                        <span>{isEditMode ? 'Save Changes' : 'Create Statement'}</span>
                    </button>
                </div>
            </div>

            <Card className="p-8">
                <form id="statement-form" onSubmit={handleSave} className="space-y-8">
                    <FormSection title="Statement Information">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputGroup label="Statement Date">
                                <Input
                                    name="statement_date"
                                    type="date"
                                    max={today}
                                    value={formData.statement_date}
                                    onChange={handleInputChange}
                                />
                            </InputGroup>
                            <InputGroup label="Payment Due Date" required>
                                <Input
                                    name="due_date"
                                    type="date"
                                    max={dueDateMax}
                                    value={formData.due_date}
                                    onChange={handleInputChange}
                                    required
                                />
                            </InputGroup>
                            <InputGroup label="Interest Rate (%)">
                                <Input className={NO_SPINNER} name="interest_rate" type="number" step="0.001" value={formData.interest_rate} onChange={handleInputChange} />
                            </InputGroup>
                        </div>
                    </FormSection>

                    <FormSection title="Payment Due">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {moneyField('total_amount_due', 'Total Amount Due', true)}
                            {moneyField('principal', 'Principal')}
                            {moneyField('interest', 'Interest')}
                            {moneyField('escrow', 'Escrow')}
                            {moneyField('fees', 'Fees')}
                            {moneyField('additional_payment', 'Additional Payment')}
                            {moneyField('past_due_amount', 'Past Due Amount')}
                        </div>
                    </FormSection>

                    <FormSection title="Balances">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {moneyField('principal_balance', 'Outstanding Principal Balance')}
                            <InputGroup label="Escrow Balance">
                                <div>
                                    <MoneyInput
                                        name="escrow_balance"
                                        allowNegative
                                        value={formData.escrow_balance}
                                        onChange={handleInputChange}
                                    />
                                    <p className="mt-1 text-xs text-[var(--text-muted)]">
                                        Enter a negative value for an escrow shortage (deficit).
                                    </p>
                                </div>
                            </InputGroup>
                        </div>
                    </FormSection>

                    <FormSection title="Notes">
                        <InputGroup label="Notes">
                            <Input name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Optional" />
                        </InputGroup>
                    </FormSection>

                    <FormSection title="Attachment">
                        {isEditMode ? (
                            existingFile ? (
                                <a
                                    href={existingFile.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-sm text-[var(--primary)] hover:underline"
                                >
                                    <FileText size={16} /> {existingFile.name}
                                </a>
                            ) : (
                                <p className="text-sm text-[var(--text-muted)]">
                                    No file attached. To attach a file, delete this statement and create a new one.
                                </p>
                            )
                        ) : (
                            <div>
                                <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                                    Statement file (image or PDF, max 10MB)
                                </label>
                                {/* Visible native browse button (not a custom
                                    button + hidden input), so this stays as a
                                    raw <input> rather than going through
                                    useFilePicker. Use bare image/* + application/pdf
                                    (no extension hints) so iOS still shows the
                                    chooser sheet — see hooks/useFilePicker for
                                    the iOS rationale. */}
                                <input
                                    type="file"
                                    accept="image/*,application/pdf"
                                    disabled={extracting}
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    className="block w-full text-sm text-[var(--text-main)] disabled:opacity-60"
                                />
                                {file && (
                                    <button
                                        type="button"
                                        onClick={() => handleAutofill(file)}
                                        disabled={extracting}
                                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-[var(--primary)] text-[var(--primary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors text-sm font-medium disabled:opacity-60"
                                    >
                                        {extracting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                        <span>{extracting ? 'Reading statement…' : 'Upload & autofill from this file'}</span>
                                    </button>
                                )}
                                <p className="mt-2 text-xs text-[var(--text-muted)]">
                                    Autofill reads the statement and pre-fills the fields above for you to review. It
                                    counts as one scan. The file stays attached when you save.
                                </p>
                            </div>
                        )}
                    </FormSection>
                </form>
            </Card>
        </div>
    );
};

export default MortgageStatementForm;
